import * as React from "react";
import { useEffect, useState } from "react";
import {
    Box,
    CircularProgress,
    Container,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Tooltip,
    Typography,
    Tabs,
    Tab,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BarChartIcon from "@mui/icons-material/BarChart";
import SearchIcon from "@mui/icons-material/Search";
import PercentIcon from "@mui/icons-material/Percent";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import LineChartComponent from "./components/LineChartComponent";
import MetricTile from "./components/MetricTile";
import { api } from "../../utils/axios";
import "./MetricsDashboard.css";
import "./searchMetrics.css"
import { Client, DistributionItem, MetricSummary, ResultsSummary, TopItem } from "../../types/metrics.types";

const formatDate = (d: Date): string => d.toISOString().split("T")[0];

const HealthStatusDot = ({ status }: { status: "ok" | "down" }) => (
    <Tooltip title={status === "ok" ? "API Healthy" : "API Down"}>
        <FiberManualRecordIcon className={`pulse-dot ${status}`} />
    </Tooltip>
);

const MetricsDashboard: React.FC = () => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const [startDate, setStartDate] = useState<string>(formatDate(yesterday));
    const [endDate, setEndDate] = useState<string>(formatDate(today));
    const [clientList, setClientList] = useState<Client[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<string>("");
    const [userId, setUserId] = useState<string>("");
    const [userIdInput, setUserIdInput] = useState<string>("");

    const [summary, setSummary] = useState<MetricSummary | null>(null);
    const [distribution, setDistribution] = useState<DistributionItem[]>([]);
    const [resultsSummary, setResultsSummary] = useState<ResultsSummary>({
        total_searches: 0,
        percentage_with_results: 0,
    });
    const [topLocations, setTopLocations] = useState<TopItem[]>([]);
    const [topSpecialties, setTopSpecialties] = useState<TopItem[]>([]);
    const [topConditions, setTopConditions] = useState<TopItem[]>([]);
    const [topProcedures, setTopProcedures] = useState<TopItem[]>([]);
    const [searches, setSearches] = useState<any[]>([]);

    const [loading, setLoading] = useState<boolean>(true);
    const [healthStatus, setHealthStatus] = useState<"ok" | "down">("down");
    const [activeTab, setActiveTab] = useState(0);

    const handleTabChange = (_: any, newValue: number) => setActiveTab(newValue);

    useEffect(() => {
        const fetchClients = async () => {
            try {
                const res = await api.get<Client[]>("/clients");
                const uniqueClients = Array.from(new Map(res.data.map((c) => [c.client_name, c])).values());
                setClientList(uniqueClients);
            } catch (err) {
                console.error("Failed to fetch client list", err);
            }
        };
        fetchClients();
    }, []);

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                setLoading(true);
                const params = `start_date=${startDate}&end_date=${endDate}`
                    + (selectedClientId ? `&client_id=${selectedClientId}` : "")
                    + (userId ? `&userid=${userId}` : "");

                const [s, r, d] = await Promise.all([
                    api.get<MetricSummary>(`/metrics/summary?${params}`),
                    api.get<ResultsSummary>(`/metrics/results-summary?${params}`),
                    api.get<DistributionItem[]>(`/metrics/summary-distribution?${params}`),
                ]);

                setSummary(s.data);
                setResultsSummary(r.data);
                setDistribution(d.data);
            } catch (err) {
                console.error("Failed to fetch summary data", err);
            } finally {
                setLoading(false);
            }
        };
        fetchSummary();
    }, [startDate, endDate, selectedClientId, userId]);

    useEffect(() => {
        const fetchTopFields = async () => {
            const params = `start_date=${startDate}&end_date=${endDate}`
                + (selectedClientId ? `&client_id=${selectedClientId}` : "")
                + (userId ? `&userid=${userId}` : "");

            try {
                const [loc, spec, cond, proc] = await Promise.all([
                    api.get<TopItem[]>(`/metrics/locations?${params}`),
                    api.get<TopItem[]>(`/metrics/specialties?${params}`),
                    api.get<TopItem[]>(`/metrics/conditions?${params}`),
                    api.get<TopItem[]>(`/metrics/procedures?${params}`),
                ]);

                setTopLocations(loc.data.slice(0, 5));
                setTopSpecialties(spec.data.slice(0, 5));
                setTopConditions(cond.data.slice(0, 5));
                setTopProcedures(proc.data.slice(0, 5));
            } catch (err) {
                console.error("Failed to fetch top fields", err);
            }
        };
        fetchTopFields();
    }, [startDate, endDate, selectedClientId, userId]);

    useEffect(() => {
        const fetchSearchResults = async () => {
            if (activeTab !== 1) return;
            try {
                const params = `start_date=${startDate}&end_date=${endDate}`
                    + (selectedClientId ? `&client_id=${selectedClientId}` : "")
                    + (userId ? `&userid=${userId}` : "");
                const res = await api.get<any[]>(`/metrics/searches-with-results?${params}`);
                setSearches(res.data);
            } catch (err) {
                console.error("Failed to fetch search results", err);
            }
        };
        fetchSearchResults();
    }, [activeTab, startDate, endDate, selectedClientId, userId]);

    useEffect(() => {
        const checkHealth = async () => {
            try {
                const res = await api.get<{ status: string }>("/health");
                setHealthStatus(res.data.status === "Ok" ? "ok" : "down");
            } catch {
                setHealthStatus("down");
            }
        };
        checkHealth();
        const interval = setInterval(checkHealth, 10000);
        return () => clearInterval(interval);
    }, []);

    const metricTiles = [
        { title: "Active Users", value: summary?.unique_users || 0, icon: <PeopleIcon fontSize="large" /> },
        { title: "Success Rate", value: `${summary?.success_rate || 0}%`, icon: <CheckCircleIcon fontSize="large" /> },
        { title: "Avg Interactions/User", value: summary?.avg_per_user || 0, icon: <BarChartIcon fontSize="large" /> },
        { title: "Total Searches", value: resultsSummary.total_searches || 0, icon: <SearchIcon fontSize="large" /> },
        { title: "% With Results", value: `${resultsSummary.percentage_with_results || 0}%`, icon: <PercentIcon fontSize="large" /> },
    ];

    const renderTopList = (title: string, items: TopItem[]) => (
        <Paper elevation={3} className="top-list-card">
            <Typography variant="h6" className="top-list-title">{title}</Typography>
            <Divider className="top-list-divider" />
            <List dense>
                {items.map((item, index) => (
                    <ListItem key={index} disablePadding className="top-list-item">
                        <ListItemText
                            primary={<Typography variant="subtitle1" className="top-list-value">{item.value}</Typography>}
                            secondary={<Typography variant="caption" color="text.secondary">Searches: {item.count}</Typography>}
                        />
                    </ListItem>
                ))}
            </List>
        </Paper>
    );

    return (
        <Container maxWidth="xl">
            <Box p={2}>
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                    <Typography variant="h4" fontWeight={700}>📊 Metrics Dashboard</Typography>
                    <HealthStatusDot status={healthStatus} />
                </Box>

                <Grid container spacing={2} alignItems="center" className="filters">
                    <Grid item>
                        <TextField label="Start Date" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                    </Grid>
                    <Grid item>
                        <TextField label="End Date" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                    </Grid>
                    <Grid item>
                        <TextField select value={selectedClientId} onChange={e => setSelectedClientId(e.target.value)} size="small" SelectProps={{ native: true }}>
                            <option value="">All Clients</option>
                            {clientList.map(client => (
                                <option key={client.client_id} value={client.client_id}>{client.client_name}</option>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item>
                        <TextField label="User ID" value={userIdInput} onChange={e => setUserIdInput(e.target.value)} placeholder="Enter user ID" size="small" />
                    </Grid>
                    <Grid item>
                        <button className="search-button" onClick={() => setUserId(userIdInput)}>Search</button>
                    </Grid>
                </Grid>

                <Tabs value={activeTab} onChange={handleTabChange} sx={{ mt: 3 }}>
                    <Tab label="Summary" />
                    <Tab label="Searches" />
                </Tabs>

                {loading ? (
                    <Box textAlign="center" mt={4}><CircularProgress /></Box>
                ) : (
                    <>
                        {activeTab === 0 && (
                            <>
                                <Grid container spacing={3}>
                                    {metricTiles.map((tile, index) => (
                                        <Grid item xs={12} sm={6} md={4} key={index}>
                                            <MetricTile {...tile} />
                                        </Grid>
                                    ))}
                                </Grid>

                                <Grid container spacing={3} sx={{ mt: 4 }}>
                                    <Grid item xs={12} md={4}>
                                        <LineChartComponent data={distribution} dataKey="unique_users" title="Active Users Over Time" />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <LineChartComponent
                                            data={distribution.map(d => ({
                                                ...d,
                                                success_rate: d.success ? (d.success / (d.success + d.failure)) * 100 : 100
                                            }))}
                                            dataKey="success_rate"
                                            title="Success Rate Over Time"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <LineChartComponent data={distribution} dataKey="avg_per_user" title="Avg Interactions/User Over Time" />
                                    </Grid>
                                </Grid>

                                <Grid container spacing={3} sx={{ mt: 4 }}>
                                    <Grid item xs={12} md={6}>{renderTopList("Top 5 Locations", topLocations)}</Grid>
                                    <Grid item xs={12} md={6}>{renderTopList("Top 5 Specialties", topSpecialties)}</Grid>
                                    <Grid item xs={12} md={6}>{renderTopList("Top 5 Conditions", topConditions)}</Grid>
                                    <Grid item xs={12} md={6}>{renderTopList("Top 5 Procedures", topProcedures)}</Grid>
                                </Grid>
                            </>
                        )}

                        {activeTab === 1 && (
                            <Box mt={3}>
                                <Typography variant="h6" fontWeight={600} mb={2}>Searches with Results</Typography>
                                <Paper sx={{ overflowX: "auto" }}>
                                    <table className="search-table">
                                        <thead>
                                        <tr>
                                            <th>Location</th>
                                            <th>Lat/Long</th>
                                            <th>Age</th>
                                            <th>Condition</th>
                                            <th>Specialty</th>
                                            <th>Procedure</th>
                                            <th>Identified Doctor</th>
                                            <th>Recommended</th>
                                            <th>Rating</th>
                                            <th>Distance</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {searches.map((s, i) => (
                                            <tr key={i}>
                                                <td>{s.location_searched}</td>
                                                <td>{s.location_searched_lat_long}</td>
                                                <td>{s.patient_age}</td>
                                                <td>{s.condition_name}</td>
                                                <td>{s.specialty}</td>
                                                <td>{s.procedure_name}</td>
                                                <td>{s.identified_doctor}</td>
                                                <td>{s.recommended_results}</td>
                                                <td>{s.recommended_result_rating}</td>
                                                <td>{s.recommended_doctor_distance}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </Paper>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Container>
    );
};

export default MetricsDashboard;
